import { authorizedServerFetch } from '@/actions/server.actions';
import useUserProfile from '@/hooks/useUserProfile';
import Typography from '@ui/Typography';
import React, { useEffect, useState } from 'react';
import * as yup from 'yup';
import Avatar from '@/components/next-ui/Avatar';
import Button from '@/components/next-ui/Button';
import TextArea from '@/components/next-ui/TextArea';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { switchUserCommunity } from '@/actions/switch-community.action';

const schema = yup.object().shape({
  message: yup
    .string()
    .required('Please provide a brief message')
    .min(10, 'Message must be at least 10 characters')
    .max(255, 'Message must be at most 255 characters'),
});

function UserJoinNonProfitForm(props) {
  const { nonProfit, stepFromStore } = props;
  const userProfile = useUserProfile();
  const [step, setStep] = useState(stepFromStore || 'about');

  const communityExists = userProfile?.communities?.find(
    (c) => c.communityId === nonProfit?.company?.id,
  );

  const form = useForm({
    defaultValues: {
      message: '',
    },
    mode: 'onChange',
    resolver: yupResolver(schema),
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = form;

  const handleChangeCommunity = async (communityId) => {
    if (communityId) {
      switchUserCommunity(communityId);
    }
  };

  const submitApplication = async (data) => {
    const { message } = data;

    const res = await authorizedServerFetch(
      `users/me/nonprofit/${nonProfit.id}/apply`,
      {
        method: 'POST',
        body: JSON.stringify({
          message,
        }),
      },
    );

    if (res?.ok) {
      setStep('success');
    } else {
      if (res?.status === 409) {
        setStep('error');
      }
    }
  };

  const handleFormSubmit = (data) => {
    submitApplication(data);
  };

  const handleCancel = () => {
    setStep('about');
  };

  if (communityExists) {
    return (
      <div className="flex w-full flex-1 flex-col gap-12">
        <Typography variant="h1" component="h2">
          Welcome back, {userProfile.firstName || 'User'}!
        </Typography>
        <Typography variant="body1">
          You are already a valued member of the {nonProfit?.name} community. We
          appreciate your continued involvement and commitment.
        </Typography>
        <Typography variant="body1">
          Feel free to explore the community and stay connected with ongoing
          initiatives and updates.
        </Typography>
        <Button
          variant="filled"
          color="primary"
          onClick={() => handleChangeCommunity(nonProfit?.company_id)}>
          Continue to {nonProfit?.company?.name || 'the community'}
        </Button>
      </div>
    );
  }

  if (step === 'success') {
    return (
      <div className="flex w-full flex-1 flex-col gap-12">
        <Typography variant="h1" component="h2">
          Congratulations, {userProfile.firstName || 'User'}!
        </Typography>
        <Typography variant="body1">
          {nonProfit?.company?.visibilityType === 'public'
            ? `Welcome aboard! We are thrilled to have you join ${
                nonProfit?.company?.name || nonProfit?.name
              } nonprofit community - Let's make a difference together.`
            : `Thank you for joining ${
                nonProfit?.company?.name || nonProfit?.name
              }! Your request is submitted and pending approval by the Nonprofit Admin. We'll notify you once it's approved.`}
        </Typography>
        {nonProfit?.company?.visibilityType === 'public' && (
          <Button
            variant="filled"
            color="primary"
            onClick={() => handleChangeCommunity(nonProfit?.company_id)}>
            Explore {nonProfit?.company?.name || 'community'}
          </Button>
        )}
      </div>
    );
  }

  if (step === 'error') {
    return (
      <div className="flex w-full flex-1 flex-col gap-12">
        <Typography variant="h1" component="h2">
          Application Error
        </Typography>
        <Typography variant="body1">
          Thank you for your interest! It seems that your application has
          already been submitted. Kindly verify whether you are already a member
          of our community or await further approval.
        </Typography>
      </div>
    );
  }

  if (step === 'backend-error') {
    return (
      <div className="flex w-full flex-1 flex-col gap-12">
        <Typography variant="h1" component="h2">
          Oops... Something went wrong!
        </Typography>
        <Typography variant="body1">
          Please comeback and try again later or contact us at hello@neki.io;
        </Typography>
      </div>
    );
  }

  // if (nonProfit?.company?.visibilityType === 'private') {
  return (
    <div className="flex w-full flex-1 flex-col gap-12">
      {/* <Typography variant="h1">
          Join {(nonProfit?.name || 'nonprofit')?.toUpperCase()} community!
        </Typography> */}

      <form
        onSubmit={handleSubmit(handleFormSubmit)}
        className="flex flex-col gap-6">
        <div className="flex items-center gap-4">
          <Avatar
            src={nonProfit?.company_id ? userProfile.avatar : null}
            alt={userProfile.firstName}
          />
          <div className="flex flex-col items-start">
            <Typography variant="subtitle1">{userProfile.fullName}</Typography>
            <Typography variant="body2">
              {[userProfile.designation, userProfile.email]
                .filter(Boolean)
                .join(' - ')}
            </Typography>
          </div>
        </div>

        <Controller
          name="message"
          control={control}
          render={({ field }) => (
            <TextArea
              placeholder="I want to join this community because..."
              required
              {...field}
              error={errors.message?.message}
            />
          )}
        />

        <div className="mt-2 flex w-full items-center justify-end">
          <Button type="submit" variant="filled" color="primary">
            Apply
          </Button>
        </div>
      </form>
    </div>
  );
  // }
}

export default UserJoinNonProfitForm;
